/**
 * Returns domain service.
 *
 * Wraps the trusted transactional return/refund/exchange RPCs. Refunds and
 * exchange orders are financial/transactional — always routed through the
 * backend, never assembled client-side.
 */

import { callRpc } from "@/lib/api/rpc";

export interface ReturnLineInput {
  product_id: string | null;
  variant_id: string | null;
  qty: number;
  condition: string;
  sales_order_item_id?: string | null;
}

export interface CreateReturnParams {
  lines: ReturnLineInput[];
  salesOrderId: string | null;
  customerId: string;
  locationId: string;
  reason: string | null;
  idempotencyKey: string;
}

export function createSalesReturn(params: CreateReturnParams): Promise<unknown> {
  return callRpc("create_sales_return", {
    p_lines: params.lines,
    p_sales_order_id: params.salesOrderId,
    p_customer_id: params.customerId,
    p_location_id: params.locationId,
    p_reason: params.reason,
    p_idempotency_key: params.idempotencyKey,
  });
}

export interface FinalizeLineInput {
  item_id: string;
  disposition: string;
  restock_qty: number;
}

export function finalizeSalesReturn(
  returnId: string,
  lines: FinalizeLineInput[],
  restockLocationId?: string | null,
): Promise<void> {
  return callRpc("finalize_sales_return", {
    p_return_id: returnId,
    p_lines: lines,
    p_restock_location_id: restockLocationId ?? null,
  });
}

export function receiveSalesReturn(returnId: string): Promise<void> {
  return callRpc("receive_sales_return", { p_return_id: returnId });
}

export function createSalesRefund(params: {
  salesOrderId: string | null;
  returnId?: string | null;
  amount?: number | null;
  reason: string | null;
  idempotencyKey: string;
  mode?: "items" | "entire_order" | "order_level";
  lines?: Array<{ salesOrderItemId: string; qty: number }>;
  shippingAmount?: number;
  adjustmentAmount?: number;
}): Promise<string> {
  return callRpc("create_sales_refund", {
    p_sales_order_id: params.salesOrderId,
    p_return_id: params.returnId ?? null,
    p_amount: params.amount ?? null,
    p_reason: params.reason,
    p_idempotency_key: params.idempotencyKey,
    p_mode: params.mode ?? "order_level",
    p_lines: (params.lines ?? []).map((l) => ({ sales_order_item_id: l.salesOrderItemId, qty: l.qty })),
    p_shipping_amount: params.shippingAmount ?? 0,
    p_adjustment_amount: params.adjustmentAmount ?? 0,
  });
}

export function cancelRefundedUnfulfilledQuantity(refundId: string): Promise<{ ok: boolean; idempotent?: boolean; qty_cancelled?: number }> {
  return callRpc("cancel_refunded_unfulfilled_quantity", { p_refund_id: refundId });
}

export function createExchangeOrder(params: {
  returnId: string;
  lines: { product_id: string; variant_id: string | null; qty: number; unit_price: number }[];
  idempotencyKey?: string;
}): Promise<string> {
  return callRpc<string>("create_exchange_order", {
    p_return_id: params.returnId,
    p_lines: params.lines,
    p_idempotency_key: params.idempotencyKey ?? undefined,
  });
}

export function recordRefundAcknowledgement(params: {
  refundId: string;
  provider: string | null;
  providerRefundId: string | null;
}): Promise<void> {
  return callRpc("record_refund_acknowledgement", {
    p_refund_id: params.refundId,
    p_provider: params.provider,
    p_provider_refund_id: params.providerRefundId,
  });
}
